import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';

import { Observable, of, timer } from 'rxjs';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ApiJob } from '../../../core/jobs/entities/api-job.entity';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { RecordType } from '../../../core/records/entities/metadata/metadata.entity';
import { DialogConfig } from '../../../dynamic-dialog/entities/dialog-config.entity';
import { DialogRef } from '../../../dynamic-dialog/entities/dialog-ref.class';
import { ActionItem } from '../../../elements/components/action-item/entities/action-item.entity';
import { ShNotificationComponent } from '../../../elements/components/notification/notification.component';
import { ProcessingProgressComponent } from '../../components/processing-progress/processing-progress.component';
import {
    COMBINED_PROCESSING_STEPS,
    RECORD_PROCESSING_STEPS,
    SIP_PROCESSING_STEPS
} from '../../constants/processing-steps.constant';
import { ProcessingProgress } from '../../entities/processing-progress.entity';
import { ProcessingStep } from '../../entities/processing-step.entity';
import { COMBINED_PROCESSING_TASKS, ProcessingTask } from '../../entities/processing-task.entity';
import { ProcessingProgressSandbox } from '../../processing-progress.sandbox';

@Component({
    selector: 'sh-processing-progress-dialog',
    templateUrl: './processing-progress-dialog.container.html',
    styleUrls: ['./processing-progress-dialog.container.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslateModule, ShNotificationComponent, ProcessingProgressComponent],
    providers: [ProcessingProgressSandbox],
    standalone: true
})
export class ProcessingProgressDialogContainerComponent extends BaseContainer implements OnInit {
    readonly #REFRESH_TIMER_IN_SECONDS: number = 5;

    readonly #record: ApiFragment;

    jobs$: Observable<ApiJob>;
    processingProgress$: Observable<ProcessingProgress>;
    actionItems: ActionItem[];

    readonly #sandbox: ProcessingProgressSandbox = inject(ProcessingProgressSandbox);

    constructor(
        public config: DialogConfig,
        public dialog: DialogRef<void>
    ) {
        super();
        this.#record = this.config.data?.record;
    }

    ngOnInit(): void {
        this.setLoading(true);
        this.initProgressProgressStream();
    }

    /**
     * Generates a simplified processing progress overview for regular users
     * This is by no means a correct and complete representation of the processing steps in the backend
     * It is based of a definition of steps stored in the PROCESSING_STEPS constant
     * This should not be used as a basis to debug failing processed records
     */
    private initProgressProgressStream(): void {
        this.processingProgress$ = timer(0, this.#REFRESH_TIMER_IN_SECONDS * 1000).pipe(
            switchMap(_ =>
                this.#sandbox.getRecordJobs(this.#record.getOriginalRecordId() ?? this.#record.getRecordId())
            ),
            catchError(this.handleJobsError.bind(this)),
            map(this.jobsToProcessingProgress.bind(this)),
            tap(_ => this.setLoading(false))
        );
    }

    private jobsToProcessingProgress(jobs: ApiJob[]): ProcessingProgress {
        if (jobs.length === 0) {
            return new ProcessingProgress([], jobs);
        }

        if (jobs.some(job => COMBINED_PROCESSING_TASKS.includes(job.TaskName as ProcessingTask))) {
            return new ProcessingProgress(COMBINED_PROCESSING_STEPS, jobs);
        }

        const steps: ProcessingStep[] =
            this.#record.getRecordType() === RecordType.Sip ? SIP_PROCESSING_STEPS : RECORD_PROCESSING_STEPS;

        return new ProcessingProgress(steps, jobs);
    }

    private handleJobsError(error: Error): Observable<ApiJob[]> {
        this.setLoading(false);
        return of([]);
    }
}
